import { getLogger } from "@expert/logging";
import { setFullStoryUserProperties } from "@expert/monitoring";
import { useEffect, useRef } from "react";

import { env } from "@/config";
import { useFeatureFlag } from "@/shared-utils/hooks/useFeatureFlag";

const logger = getLogger({ module: "useMemoryTelemetry" });

const MEMORY_TELEMETRY_FLAG = "isMemoryTelemetryEnabled";
const SAMPLE_INTERVAL_MS = 60_000;
const BYTES_PER_MB = 1024 * 1024;

type SampleReason = "initial" | "interval" | "hidden";

// `performance.memory` and `navigator.deviceMemory` are Chromium-only and absent from the DOM lib
// types, so narrow them here instead of casting to `any`. The affected fleet is Chrome; on any
// engine without them this hook is a no-op.
interface ChromePerformanceMemory {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
}

function readHeap(): ChromePerformanceMemory | undefined {
    const mem = (performance as Performance & { memory?: ChromePerformanceMemory }).memory;
    return mem && typeof mem.usedJSHeapSize === "number" ? mem : undefined;
}

function readDeviceMemoryGb(): number | undefined {
    return (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
}

// Device facts are both logged per-sample and attached to the FullStory session; read them in one
// place so the two paths can never report different values.
function readDeviceFacts() {
    return {
        deviceMemoryGb: readDeviceMemoryGb() ?? null,
        hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    };
}

const toMb = (bytes: number) => Math.round(bytes / BYTES_PER_MB);

interface MemoryTelemetryOptions {
    partner: string | undefined;
    sessionId: string;
    /** Effective layout for this session's channel. The primary dimension the pilot slices on. */
    layout: "redesigned" | "legacy";
}

/**
 * Logs one structured renderer JS-heap sample per interval (queryable in OpenSearch) and tags the
 * FullStory session with device facts (RAM, cores). Motivation: experts on low-memory machines hit
 * renderer OOM ("Aw Snap" / STATUS_BREAKPOINT) crashes, but we have no prod visibility into how much
 * RAM those machines have or how close a given session gets to the V8 heap ceiling.
 *
 * We log per-sample (rather than emit aggregated CloudWatch metrics) on purpose: diagnosing a crash
 * needs the individual session's heap trajectory — `sessionId`-keyed records let us reconstruct one
 * session's climb and line it up against its crash, which pre-aggregated metrics discard. High-
 * cardinality context (sessionId, commitHash) is natural in logs and would be costly/impossible as
 * metric dimensions.
 *
 * Caveat: `performance.memory` reports the JS heap only — it does not see native/compositor memory
 * (e.g. the Amazon Connect CCP iframe). `jsHeapSizeLimit` still reveals the isolate's ceiling, and
 * the used/limit trajectory paired with `deviceMemory` is the best available proxy.
 *
 * Interpreting results: a positive signal (a session climbing toward its heap ceiling before a
 * crash) confirms JS-heap OOM. A NEGATIVE signal does NOT exonerate the OOM hypothesis — a process
 * can hit fatal OOM in native/compositor memory (notably the CCP iframe) while this JS-heap gauge
 * still reads healthy. Treat a clean-heap reading as "look elsewhere" (native memory), not "not OOM".
 *
 * Precision: Chrome coarsens `performance.memory` (bucketed values; `jsHeapSizeLimit` capped without
 * cross-origin isolation) and throttles its updates, so `jsHeapUsedPercent` near 100% is approximate
 * — trust the trajectory shape across samples, don't over-index on a single high reading.
 *
 * Gated behind the `isMemoryTelemetryEnabled` flag (off until enabled per-partner in config) so
 * it adds no log volume fleet-wide and can be killed instantly.
 */
export function useMemoryTelemetry({ partner, sessionId, layout }: MemoryTelemetryOptions) {
    const { data: isFlagEnabled } = useFeatureFlag<boolean>(MEMORY_TELEMETRY_FLAG, { partner });

    // Latch `enabled` on once, then keep it on for the rest of the session. `partner` (from
    // usePartner) can change mid-session — the active task's partner vs the agent's — which re-keys
    // the feature-flag query and would otherwise let `enabled` flip false→true again, re-running the
    // effects and emitting a second `initial` sample (corrupting the per-session heap trajectory).
    const enabledRef = useRef(false);
    enabledRef.current ||= isFlagEnabled === true && readHeap() !== undefined;
    const enabled = enabledRef.current;

    // Latest context held in a ref so the interval callback never needs to be re-created when a
    // dependency (session, partner) changes mid-shift.
    const contextRef = useRef<Record<string, unknown>>({});
    // `channel` and `commitHash` are already attached to every log line as pino root bindings (see
    // logger-init), so they are not repeated here beyond `commitHash`, which we keep as an explicit,
    // queryable field at the sample site. `layout` is logged as a first-class field rather than
    // derived via an external join: it's the primary dimension the pilot slices on (redesigned vs
    // legacy heap trajectories), and OpenSearch has no native join, so a cross-source reconstruction
    // would be the fragile part of the exact comparison this telemetry exists to make.
    contextRef.current = {
        sessionId,
        partner: partner ?? "unknown",
        layout,
        commitHash: env.commitHash,
        ...readDeviceFacts(),
    };

    // Attach device facts to the FullStory session once, so a crashing session's replay shows the
    // device class (RAM / cores) at a glance. Ordering is safe without a reapply: `initFullStory`
    // runs at App mount, long before this flag-gated effect (the flag is an async fetch), and the
    // FullStory snippet queues post-init calls — so this lands even if the session isn't ready yet.
    useEffect(() => {
        if (!enabled) return;
        setFullStoryUserProperties(readDeviceFacts());
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;

        const sample = (sampleReason: SampleReason) => {
            const heap = readHeap();
            if (!heap) return;
            const jsHeapUsedPercent =
                heap.jsHeapSizeLimit > 0 ? Math.round((heap.usedJSHeapSize / heap.jsHeapSizeLimit) * 100) : 0;
            logger.info(
                {
                    event: "memory-sample",
                    sampleReason,
                    ...contextRef.current,
                    usedJsHeapMb: toMb(heap.usedJSHeapSize),
                    totalJsHeapMb: toMb(heap.totalJSHeapSize),
                    jsHeapLimitMb: toMb(heap.jsHeapSizeLimit),
                    jsHeapUsedPercent,
                },
                "Renderer memory sample",
            );
        };

        sample("initial");
        const intervalId = setInterval(() => sample("interval"), SAMPLE_INTERVAL_MS);
        const onVisibilityChange = () => {
            // Capture a final reading when the tab is backgrounded — the closest we can get to a
            // pre-crash sample (a hard OOM won't fire any event).
            if (document.visibilityState === "hidden") sample("hidden");
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [enabled]);
}
